if (!customElements.get('size-guide-price')) {
  class SizeGuidePrice extends HTMLElement {
    static SELECTORS = {
      saleAmount: '[data-sg-sale-amount]',
      compareAmount: '[data-sg-compare-amount]',
      sale: '[data-sg-sale]',
      compare: '[data-sg-compare]',
      plain: '[data-sg-plain]',
      qtyInput: 'quantity-selector input[type="number"]'
    };

    static CLASSES = {
      sale: 'text--xl--bold text-on-sale',
      compare: 'text--xl line-through',
      plain: 'text--xl--bold'
    };

    static EVENTS = {
      input: 'input',
      change: 'change'
    };

    static FIXED_UNITS = ['€', 'fixed'];
    static PERCENT_UNITS = ['%', 'percent'];
    static PACK_SIZE = 2;

    connectedCallback () {
      this._formId = this.dataset.formId || '';
      this._basePrice = this._toInt(this.dataset.basePrice, 0);
      this._qty1Value = this._toFloat(this.dataset.qty1DiscountValue, 0);
      this._qty1Unit = this.dataset.qty1DiscountUnit || '';
      this._qty2Value = this._toFloat(this.dataset.qty2DiscountValue, 0);
      this._qty2Unit = this.dataset.qty2DiscountUnit || '';

      this._abort = new AbortController();
      this._drawer = this.closest('x-drawer');

      this._bindQty();
      this._render(this._currentQty());
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _bindQty () {
      // The quantity-selector lives elsewhere in the drawer; query against
      // the drawer root so the +/- buttons and the input itself work.
      const root = this._drawer || document;
      const input = root.querySelector(SizeGuidePrice.SELECTORS.qtyInput);
      if (!input) return;
      this._qtyInput = input;

      input.addEventListener(SizeGuidePrice.EVENTS.input, () => this._render(this._currentQty()), { signal: this._abort.signal });
      input.addEventListener(SizeGuidePrice.EVENTS.change, () => this._render(this._currentQty()), { signal: this._abort.signal });
    }

    _currentQty () {
      const raw = this._qtyInput ? this._qtyInput.value : '1';
      return Math.max(1, this._toInt(raw, 1));
    }

    // Returns the effective PER-UNIT offer for a quantity. The qty-2 offer is a
    // pack-of-2 metaobject: a fixed € value is the saving on the whole 2-pack, so
    // we halve it to a per-unit discount; a % is already per-unit and unchanged.
    _pickOffer (qty) {
      if (qty >= 2 && this._qty2Value > 0) {
        const isFixed = SizeGuidePrice.FIXED_UNITS.includes(this._qty2Unit);
        const value = isFixed ? this._qty2Value / SizeGuidePrice.PACK_SIZE : this._qty2Value;
        return { value, unit: this._qty2Unit };
      }
      if (this._qty1Value > 0) {
        return { value: this._qty1Value, unit: this._qty1Unit };
      }
      return null;
    }

    _computeUnitPrice (offer) {
      if (!offer) return this._basePrice;
      if (SizeGuidePrice.PERCENT_UNITS.includes(offer.unit)) {
        const multiplier = (100 - offer.value) / 100;
        return Math.max(0, Math.round(this._basePrice * multiplier));
      }
      if (SizeGuidePrice.FIXED_UNITS.includes(offer.unit)) {
        return Math.max(0, this._basePrice - Math.round(offer.value * 100));
      }
      return this._basePrice;
    }

    // Flat per-unit pack rate applied to every unit, so qty 2/3/4… stay consistent.
    _computeLineTotal (qty) {
      return this._computeUnitPrice(this._pickOffer(qty)) * qty;
    }

    _render (qty) {
      const totalCompare = this._basePrice * qty;
      const totalDiscounted = this._computeLineTotal(qty);
      const isOnSale = totalDiscounted < totalCompare;

      // Re-render markup to match the on-sale vs plain state (template was
      // server-rendered for the initial state — we may need to flip it).
      const saleAmountText = this._formatMoney(totalDiscounted);
      const compareAmountText = this._formatMoney(totalCompare);

      const saleEl = this.querySelector(SizeGuidePrice.SELECTORS.sale);
      const compareEl = this.querySelector(SizeGuidePrice.SELECTORS.compare);
      const plainEl = this.querySelector(SizeGuidePrice.SELECTORS.plain);

      if (isOnSale) {
        if (plainEl) plainEl.outerHTML = `<sale-price class="${SizeGuidePrice.CLASSES.sale}" data-sg-sale><span class="sr-only"></span><span data-sg-sale-amount>${saleAmountText}</span></sale-price><compare-at-price class="${SizeGuidePrice.CLASSES.compare}" data-sg-compare><span class="sr-only"></span><span data-sg-compare-amount>${compareAmountText}</span></compare-at-price>`;
        else {
          if (saleEl) saleEl.querySelector(SizeGuidePrice.SELECTORS.saleAmount).textContent = saleAmountText;
          if (compareEl) compareEl.querySelector(SizeGuidePrice.SELECTORS.compareAmount).textContent = compareAmountText;
        }
      } else {
        if (saleEl || compareEl) {
          saleEl?.remove();
          compareEl?.remove();
          this.insertAdjacentHTML('afterbegin', `<span class="${SizeGuidePrice.CLASSES.plain}" data-sg-plain><span data-sg-sale-amount>${saleAmountText}</span></span>`);
        } else if (plainEl) {
          plainEl.querySelector(SizeGuidePrice.SELECTORS.saleAmount).textContent = saleAmountText;
        }
      }
    }

    _formatMoney (cents) {
      const fmt = window.themeVariables?.settings?.moneyFormat
        || window.theme?.moneyFormat
        || window.Shopify?.money_format;
      if (window.Shopify?.formatMoney && fmt) {
        return window.Shopify.formatMoney(cents, fmt).replace(/([.,])00(\D|$)/, '$2').trim();
      }
      const amount = cents / 100;
      const hasCents = amount % 1 !== 0;
      return amount.toLocaleString('fr-FR', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2
      }) + ' €';
    }

    _toInt (value, fallback) {
      const n = parseInt(value, 10);
      return Number.isFinite(n) ? n : fallback;
    }

    _toFloat (value, fallback) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : fallback;
    }
  }

  customElements.define('size-guide-price', SizeGuidePrice);
}
